import type { ComponentProps, ComponentPropsWithRef, ReactNode } from "react";
import { createContext, useContext } from "react";
import type { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel, { type EmblaViewportRefType } from "embla-carousel-react";
import { tv } from "tailwind-variants";

import {
  useCarouselDotsNav,
  type UseCarouselDotsNavType,
  useCarouselPrevNextNav,
  type UseCarouselPrevNextNavType,
} from "@/hooks";
import { icons } from "@/ui";

const carouselVariants = tv({
  slots: {
    root: "relative",
    slider: "overflow-auto",
    slides: "flex touch-pan-y touch-pinch-zoom",
    arrowNav:
      "absolute inset-x-0 top-1/2 flex w-full -translate-y-1/2 transform items-center justify-between px-4",
    dotsNav:
      "absolute flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-2 shadow-raised",
    prevNext:
      "flex size-11 cursor-pointer items-center justify-center rounded-full border border-line bg-surface text-content shadow-card transition-colors hover:bg-surface-hover hover:text-accent disabled:cursor-not-allowed disabled:opacity-5",
    dot: "size-2 rounded-full bg-content-subtle transition-all duration-300",
  },
});

const {
  arrowNav,
  dot,
  dotsNav,
  prevNext,
  root,
  slider,
  slides: slidesClasses,
} = carouselVariants();

type CarouselContextType = {
  slides: Slide[];
  renderSlide: (item: Slide) => ReactNode;
  prevNextNav: UseCarouselPrevNextNavType;
  dotsNav: UseCarouselDotsNavType;
  emblaRef: EmblaViewportRefType;
};

const CarouselContext = createContext<CarouselContextType | null>(null);

const useCarouselContext = <T extends Slide = Slide>() => {
  const carousel = useContext(CarouselContext);
  if (!carousel) {
    throw Error("useCarouselContext can only be used inside a CarouselContext.Provider");
  }

  return carousel as CarouselContextType & {
    slides: T[];
    renderSlide: (item: T) => ReactNode;
  };
};

export type Slide = {
  id: number;
};

type RootProps<T> = ComponentProps<"div"> & {
  slides: T[];
  options?: EmblaOptionsType;
  renderSlide: (item: T) => ReactNode;
};

const Root = <T extends Slide>({
  children,
  className,
  options,
  renderSlide,
  slides,
  ...props
}: RootProps<T>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const prevNextNav = useCarouselPrevNextNav(emblaApi);
  const dotsNav = useCarouselDotsNav(emblaApi);

  return (
    <CarouselContext.Provider
      value={{
        renderSlide: renderSlide as (item: Slide) => ReactNode,
        slides: slides as Slide[],
        prevNextNav,
        dotsNav,
        emblaRef,
      }}
    >
      <section className={root({ className })} {...props}>
        {children}
      </section>
    </CarouselContext.Provider>
  );
};

const Slider = ({ children, className, ...props }: ComponentProps<"div">) => {
  const { emblaRef } = useCarouselContext();

  return (
    <div className={slider({ className })} ref={emblaRef} {...props}>
      {children}
    </div>
  );
};

const Slides = ({ className, ...props }: ComponentProps<"div">) => {
  const { renderSlide, slides } = useCarouselContext();

  return (
    <div className={slidesClasses({ className })} {...props}>
      {slides.map(renderSlide)}
    </div>
  );
};

const ArrowNavigation = ({ children, className, ...props }: ComponentProps<"div">) => {
  return (
    <div className={arrowNav({ className })} {...props}>
      {children}
    </div>
  );
};

const DotsNavigation = ({ className, ...props }: ComponentProps<"div">) => {
  const {
    dotsNav: { onDotButtonClick, scrollSnaps, selectedIndex },
  } = useCarouselContext();

  return (
    <div className={dotsNav({ className })} {...props}>
      {scrollSnaps.map((_, index) => {
        return (
          <DotButton
            className={dot({
              className: index === selectedIndex && "w-6 bg-content",
            })}
            key={index}
            onClick={() => {
              return onDotButtonClick(index);
            }}
          />
        );
      })}
    </div>
  );
};

const PrevButton = ({ className }: Pick<ComponentPropsWithRef<"button">, "className">) => {
  const {
    prevNextNav: { onPrevButtonClick, prevBtnDisabled },
  } = useCarouselContext();

  return (
    <button
      className={prevNext({ className })}
      disabled={prevBtnDisabled}
      onClick={onPrevButtonClick}
      type="button"
    >
      <icons.ChevronLeftIcon className="size-5" />
    </button>
  );
};

const NextButton = ({ className }: Pick<ComponentPropsWithRef<"button">, "className">) => {
  const {
    prevNextNav: { nextBtnDisabled, onNextButtonClick },
  } = useCarouselContext();

  return (
    <button
      className={prevNext({ className })}
      disabled={nextBtnDisabled}
      onClick={onNextButtonClick}
      type="button"
    >
      <icons.ChevronRightIcon className="size-5" />
    </button>
  );
};

const DotButton = ({ className, ...props }: ComponentPropsWithRef<"button">) => {
  return <button className={dot({ className })} {...props} />;
};

export const Carousel = {
  Root,
  Slider,
  Slides,
  ArrowNavigation,
  DotsNavigation,
  PrevButton,
  NextButton,
  DotButton,
};
